import React, { useEffect, useMemo } from "react";
import moment from "moment";
import styled from "styled-components";
import { DialogActions, DialogContent, DialogTitle, Link } from "@material-ui/core";
import { CheckCircleRounded } from "@material-ui/icons";
import { TYPEFORM_IDS } from "common/constants";
import { WorkspacePlanTier, WorkspaceUserRole } from "common/interfaces";
import stripeProducts from "common/stripeProducts";
import WorkspaceController, { WorkspaceControllerState } from "js/controllers/WorkspaceController";
import BillingController, { BillingControllerState } from "js/controllers/BillingController";
import Analytics from "js/analytics";
import { getStaticUrl } from "js/config";
import { pluralize } from "js/core/utilities/utilities";
import getLogger, { LogGroup } from "js/core/logger";
import { app } from "js/namespaces";
import { Button } from "js/Components/Button";
import {
    BeautifulDialog,
    ShowConfirmationDialog,
    ShowDialog,
    ShowDialogAsync,
    ShowErrorDialog,
} from "js/react/components/Dialogs/BaseDialog";
import { Gap10, Gap20 } from "js/react/components/Gap";
import { ShowTypeformDialog } from "js/react/components/Dialogs/TypeformDialog";
import GuestWorkspaceDialog from "js/react/components/Dialogs/GuestWorkspaceDialog";
import { FlexBox } from "js/react/components/LayoutGrid";
import { defaultText, largeTitle, smallText } from "js/textStyles";
import { themeColors } from "js/react/sharedStyles";
import withProgressDialog from "js/core/utilities/withProgressDialog";
import EndTrialDialog from "js/react/views/Billing/dialogs/EndTrialDialog";
import ChangePaymentMethodDialog from "js/react/views/Billing/dialogs/ChangePaymentMethodDialog";
import UpgradeToTeamWorkspaceDialog from "js/react/views/Billing/dialogs/UpgradeToTeamWorkspaceDialog";
import UpgradeToProWorkspaceDialog from "js/react/views/Billing/dialogs/UpgradeToProWorkspaceDialog";
import { HUBSPOT_SCRIPT_URL, HUBSPOT_SCRIPT_ID, HUBSPOT_REGION, HUBSPOT_PORTAL_ID, HUBSPOT_ENTERPRISE_FORM_ID } from "js/config";
const logger = getLogger(LogGroup.BILLING);

const StyledBeautifulDialog = styled(BeautifulDialog)`
    .MuiDialog-paperWidthSm {
        max-width: 700px;
        padding: 0;
    }
`;

enum PlanButtonAction {
    CANCEL_PLAN = "Cancel plan",
    CANCEL_TRIAL = "Cancel trial",
    CONTACT_SALES = "Contact Sales",
    CURRENT_PLAN_NOOP = "Current plan",
    DOWNGRADE_PLAN = "Contact Support",     // TODO: Change to "Downgrade" when it is implemented
    UPGRADE_PLAN = "Upgrade",
    REACTIVATE_PLAN = "Reactivate plan",
}

const StyledDialogTitle = styled(DialogTitle)`
    padding-bottom: 0;
    
    h2 {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 5px;
        padding-top: 6px;
        
        p {
            ${defaultText}
            color: ${themeColors.mediumGray};
            margin: 0;
        }
    }
`;

const StyledDialogContent = styled(DialogContent)`
    &.MuiDialogContent-root {
        display: flex;
        flex-direction: row;
        justify-content: center;
        padding: 0;
    }
`;

const StyledDialogActions = styled(DialogActions)`
    && {
        padding-top:16px;
        padding-right: 38px;
        padding-bottom: 28px;
    }
`;

const PlanName = styled.div`
    color: #333;
    font-family: "Source Sans Pro";
    font-size: 16px;
    font-style: normal;
    font-weight: 600;
    line-height: 120%;
`;

const PlanDescriptionGrid = styled.div`
    display: grid;
    grid-template-columns: auto 1fr;
    grid-template-rows: auto auto;
    align-items: center;
`;

const PlanDescriptionFirstRow = styled.div`
    color: #666;
    font-family: Source Sans Pro;
    font-size: 11px;
    font-style: normal;
    font-weight: 600;
    line-height: 120%;
`;

const PlanDescriptionSecondRow = styled.div`
    color: #666;
    font-family: Source Sans Pro;
    font-size: 11px;
    font-style: normal;
    font-weight: 400;
    line-height: 120%;
`;

const PlanDescriptionOnlyRow = styled.div`
    display:flex;
    align-items: flex-end;
    color: #666;
    font-family: Source Sans Pro;
    font-size: 11px;
    font-style: normal;
    font-weight: 600;
    line-height: 120%;
    min-height: 28px;
    vertical-align: bottom;
    padding-bottom: 2px;
`;

const PriceCell = styled.div<{ plan: WorkspacePlanTier }>`
    grid-row: span 2;
    ${largeTitle}
    color: ${props => props.plan === WorkspacePlanTier.PRO ? "#11A9E2" : "#168EF9"};
    margin-right: 8px;
`;

const PlanFeature = styled.div`
    ${smallText}
`;

const ProIcon = styled(CheckCircleRounded)`
    &.MuiSvgIcon-colorPrimary {
        color: #11A9E2;
        width: 16px;
        height: 16px;
        margin-right: 8px;
    }
`;

const TeamIcon = styled(ProIcon)`
    &.MuiSvgIcon-colorPrimary {
        color: #1180E2;
    }
`;

const EnterpriseIcon = styled(ProIcon)`
    &.MuiSvgIcon-colorPrimary {
        color: #1157E2;
    }
`;

const ProActionButton = styled(Button) <{ action: PlanButtonAction }>`
    ${({ action }) => {
        switch (action) {
            case PlanButtonAction.CANCEL_PLAN:
                return `
                    background-color: #FB0000;
                    color: white;
                `;
            case PlanButtonAction.CANCEL_TRIAL:
                return `
                    background-color: #FB0000;
                    color: white;
                `;
            case PlanButtonAction.CURRENT_PLAN_NOOP:
                return `
                    background-color: #F1F1F1;
                    color: #A9A9A9;
                `;
            default:
                return `background-color: #11A9E2;`;
        }
    }}
    /* Remove hover and pointer interaction when disabled */
    &:disabled,
    &[disabled] {
        pointer-events: none;
        cursor: default;
    }

    &:disabled:hover,
    &[disabled]:hover {
        background-color: inherit;
        color: inherit;
        filter: none;
        box-shadow: none;
    }
`;

const TeamActionButton = styled(Button) <{ action: PlanButtonAction }>`
    ${({ action }) => {
        switch (action) {
            case PlanButtonAction.CANCEL_PLAN:
                return `
                    background-color: #FB0000;
                    color: white;
                `;
            case PlanButtonAction.CANCEL_TRIAL:
                return `
                    background-color: #FB0000;
                    color: white;
                `;
            case PlanButtonAction.CURRENT_PLAN_NOOP:
                return `
                    background-color: #F1F1F1;
                    color: #A9A9A9;
                `;
            default:
                return `background-color: #1180E2;`;
        }
    }}

    /* Remove hover and pointer interaction when disabled */
    &:disabled,
    &[disabled] {
        pointer-events: none;
        cursor: default;
    }

    &:disabled:hover,
    &[disabled]:hover {
        background-color: inherit;
        color: inherit;
        filter: none;
        box-shadow: none;
    }`;

const EnterpriseActionButton = styled(Button)`
    background-color: #1157E2;
`;

const StartSubscriptionLink = styled(Link)`
    cursor: pointer;
`;

export interface ChangePlanDialogProps {
    closeDialog?: () => void;
    featuredPlan?: WorkspacePlanTier;
    analytics?: {
        cta: string;
    };
}

type CombinedProps = ChangePlanDialogProps & WorkspaceControllerState & BillingControllerState;
const ChangePlanDialog = (props: CombinedProps) => {
    const {
        stripeData,
        plan: { tier },
        closeDialog,
        featuredPlan,
        analytics
    } = props;

    if (WorkspaceController.role === WorkspaceUserRole.GUEST) {
        return <GuestWorkspaceDialog open={true} closeDialog={closeDialog} />;
    }

    const { subscription } = stripeData;
    const subscriptionCanceled = subscription?.cancel_at_period_end;
    const isInTrial = subscription?.status === "trialing";
    const daysRemainingInTrial = isInTrial ? Math.ceil(
        (subscription?.trial_end * 1000 - new Date().getTime()) / 1000 / 60 / 60 / 24
    ) : null;
    const nextBillDate = moment.unix(subscription?.current_period_end).format("MMM DD, YYYY");

    const proPrice = stripeProducts[WorkspacePlanTier.PRO].plans.find(plan => plan.type === "recurring" && plan.interval === "year" && plan.isDefault)?.amount / 100;
    const teamPrice = stripeProducts[WorkspacePlanTier.TEAM].plans.find(plan => plan.type === "recurring" && plan.interval === "year" && plan.isDefault)?.amount / 100;

    const proHighlighted = (featuredPlan === WorkspacePlanTier.PRO) ||
        (!featuredPlan && [WorkspacePlanTier.BASIC, WorkspacePlanTier.PRO].includes(tier));

    useEffect(() => {
        Analytics.ModifyWorkspacePlan.ManagePlanDialogOpen({ featured_plan: featuredPlan, analytics });
    }, []);

    const handleCancelPlan = async () => {
        Analytics.ModifyWorkspacePlan.CancelFlowStart();
        closeDialog();
        const planName = isInTrial ? "trial" : `${tier} plan`;
        let teamMessage = "";
        if (tier == "team") {
            teamMessage = "Please ensure your team transfers any presentations you'd like to continue to access to another workspace before the end of your trial.";
        }
        const accepted = await ShowConfirmationDialog({
            title: `Are you sure you want to cancel your ${planName}?`,
            message: <>
                <p>Your plan will remain active until the end of
                    the {isInTrial ? "trial period" : "current billing cycle"} on {nextBillDate} after which you will be
                    downgraded to the Basic (free) plan.</p>
                {!!teamMessage && <p>{teamMessage}</p>}
            </>,
            cancelButtonLabel: "Never mind",
            okButtonLabel: `Cancel ${planName}`,
        });

        if (!accepted) {
            return ShowDialog(ChangePlanDialog, props);
        }

        try {
            await withProgressDialog({
                title: `Canceling ${planName}...`,
                action: () => BillingController.cancelSubscription()
            });

            Analytics.ModifyWorkspacePlan.CancelFlowComplete();
            closeDialog();
            ShowTypeformDialog({
                typeformId: TYPEFORM_IDS.CHURN_SURVEY,
                hiddenFields: {
                    user_id: app.user.id,
                },
            });
        } catch (err) {
            logger.error(err, "[CurrentPlanBlock] handleCancelPlan() failed");

            ShowErrorDialog({
                error: `An error occurred while canceling your ${planName}`,
                message: (
                    <>
                        <p><strong>Error: </strong>{err.message}</p>
                        <p>We apologize for the inconvenience. Please contact us at support@beautiful.ai.</p>
                    </>
                ),
            });
        }
    };

    const handleReactivatePlan = async () => {
        Analytics.ModifyWorkspacePlan.ReactivateFlowStart();
        try {
            if (!subscription?.default_payment_method || subscription?.status === "past_due") {
                const shouldReactivate = await ShowDialogAsync(ChangePaymentMethodDialog);
                if (!shouldReactivate) {
                    return;
                }
            }

            await withProgressDialog({
                title: `Reactivating ${tier} plan...`,
                action: () => BillingController.reactivateSubscription(),
            });

            Analytics.ModifyWorkspacePlan.ReactivateFlowComplete();
        } catch (err) {
            logger.error(err, "[CurrentPlanBlock] handleReactivatePlan() failed");

            ShowErrorDialog({
                error: `An error occurred while reactivating your ${tier} plan`,
                message: (
                    <>
                        <p><strong>Error: </strong>{err.message}</p>
                        <p>We apologize for the inconvenience. Please contact us at support@beautiful.ai.</p>
                    </>
                ),
            });
        }
    };

    const SalesFormDialog = (props: { closeDialog?: () => void }) => {
        useEffect(() => {
            if (document.getElementById(HUBSPOT_SCRIPT_ID)) {
                return;
            }

            const script = document.createElement("script");
            script.src = HUBSPOT_SCRIPT_URL;
            script.id = HUBSPOT_SCRIPT_ID;
            script.defer = true;
            document.body.appendChild(script);
        }, []);

        return (
            <BeautifulDialog
                closeDialog={props.closeDialog}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>Contact Sales</DialogTitle>
                <DialogContent>
                    <div
                        className="hs-form-frame"
                        data-region={HUBSPOT_REGION}
                        data-portal-id={HUBSPOT_PORTAL_ID}
                        data-form-id={HUBSPOT_ENTERPRISE_FORM_ID}
                    ></div>
                </DialogContent>
            </BeautifulDialog>
        );
    };

    const handleDowngradePlan = async () => {
        // TODO: Send DowngradeIntent event
        window.open("mailto:support@beautiful.ai", "_blank");
    };

    const handleUpgradePlan = async (targetTier: WorkspacePlanTier) => {
        Analytics.ModifyWorkspacePlan.UpgradeFlowStart();

        closeDialog();

        if (targetTier === WorkspacePlanTier.PRO) {
            await ShowDialogAsync(UpgradeToProWorkspaceDialog, {
                onClose: result => {
                    if (!result) {
                        ShowDialog(ChangePlanDialog, props);
                    }
                }
            });
        } else if (targetTier === WorkspacePlanTier.TEAM) {
            ShowDialogAsync(UpgradeToTeamWorkspaceDialog, {
                onClose: result => {
                    if (!result) {
                        ShowDialog(ChangePlanDialog, props);
                    }
                }
            });
        }
    };

    const handleContactSales = () => {
        Analytics.ModifyWorkspacePlan.EnterpriseInquiry();
        closeDialog();
        ShowDialog(SalesFormDialog);
    };

    const handleProButtonAction = (action: PlanButtonAction) => {
        switch (action) {
            case PlanButtonAction.CANCEL_PLAN:
                return handleCancelPlan();
            case PlanButtonAction.CANCEL_TRIAL:
                return handleCancelPlan();
            case PlanButtonAction.CONTACT_SALES:
                return handleContactSales();
            case PlanButtonAction.CURRENT_PLAN_NOOP:
                return;
            case PlanButtonAction.DOWNGRADE_PLAN:
                return handleDowngradePlan();
            case PlanButtonAction.UPGRADE_PLAN:
                return handleUpgradePlan(WorkspacePlanTier.PRO);
            case PlanButtonAction.REACTIVATE_PLAN:
                return handleReactivatePlan();
        }
    };

    const handlePlanButtonAction = (action: PlanButtonAction) => {
        switch (action) {
            case PlanButtonAction.CANCEL_PLAN:
                return handleCancelPlan();
            case PlanButtonAction.CANCEL_TRIAL:
                return handleCancelPlan();
            case PlanButtonAction.CONTACT_SALES:
                return handleContactSales();
            case PlanButtonAction.CURRENT_PLAN_NOOP:
                return;
            case PlanButtonAction.DOWNGRADE_PLAN:
                return handleDowngradePlan();
            case PlanButtonAction.UPGRADE_PLAN:
                return handleUpgradePlan(WorkspacePlanTier.TEAM);
            case PlanButtonAction.REACTIVATE_PLAN:
                return handleReactivatePlan();
        }
    };

    const getTitleText = () => {
        if (!featuredPlan) {
            return "Choose the plan that's right for you.";
        } else if (featuredPlan === WorkspacePlanTier.TEAM) {
            return "Unlock this feature when you upgrade to the Team plan.";
        } else {
            return "Unlock this feature when you upgrade to the Pro plan.";
        }
    };

    const getProButtonAction = (): PlanButtonAction => {
        switch (tier) {
            case WorkspacePlanTier.BASIC:
                return PlanButtonAction.UPGRADE_PLAN;
            case WorkspacePlanTier.PRO:
                if (featuredPlan === WorkspacePlanTier.TEAM && !subscriptionCanceled) {
                    return PlanButtonAction.CURRENT_PLAN_NOOP;
                } if (featuredPlan === WorkspacePlanTier.TEAM && subscriptionCanceled) {
                    return PlanButtonAction.REACTIVATE_PLAN;
                } else if (subscriptionCanceled) {
                    return PlanButtonAction.REACTIVATE_PLAN;
                } else if (isInTrial) {
                    return PlanButtonAction.CANCEL_TRIAL;
                } else {
                    return PlanButtonAction.CANCEL_PLAN;
                }
            case WorkspacePlanTier.TEAM:
                return PlanButtonAction.DOWNGRADE_PLAN;
            case WorkspacePlanTier.ENTERPRISE:
                return PlanButtonAction.CONTACT_SALES;
        }
    };

    const getTeamButtonAction = (): PlanButtonAction => {
        switch (tier) {
            case WorkspacePlanTier.BASIC:
                return PlanButtonAction.UPGRADE_PLAN;
            case WorkspacePlanTier.PRO:
                return PlanButtonAction.UPGRADE_PLAN;
            case WorkspacePlanTier.TEAM:
                if (subscriptionCanceled) {
                    return PlanButtonAction.REACTIVATE_PLAN;
                } else if (isInTrial) {
                    return PlanButtonAction.CANCEL_TRIAL;
                } else {
                    return PlanButtonAction.CANCEL_PLAN;
                }
            case WorkspacePlanTier.ENTERPRISE:
                return PlanButtonAction.CONTACT_SALES;
        }
    };

    const handleStartSubscription = async () => {
        closeDialog();
        const subscriptionStarted = await ShowDialogAsync(EndTrialDialog, {});
        if (!subscriptionStarted) {
            ShowDialog(ChangePlanDialog, props);
        }
    };

    const FEATURES_BY_PLAN: Record<Exclude<WorkspacePlanTier, WorkspacePlanTier.BASIC>, string[]> = {
        [WorkspacePlanTier.PRO]: [
            "Generate slides and presentations with AI",
            "Hundreds of Smart Slide templates",
            "Auto-formatting layouts",
            "Stunning data visualization",
            "Customize themes and styles",
            "Image and icon libraries",
        ],
        [WorkspacePlanTier.TEAM]: [
            "Everything in Pro",
            "Collaborative workspace",
            "Viewer analytics and tracking",
            "Centralized slide and template library",
            "Shared asset library",
            "SSO (Single Sign-on)",
            "Priority support",
        ],
        [WorkspacePlanTier.ENTERPRISE]: [
            "Everything in Team",
            "Unlimited Team Resources",
            "Advanced permissions",
            "User Provisioning (SCIM)",
            "Audit Events",
            "Dedicated Onboarding",
        ],
    };

    const proButtonAction = useMemo(() => getProButtonAction(), [tier, subscriptionCanceled, isInTrial, featuredPlan]);
    const teamButtonAction = useMemo(() => getTeamButtonAction(), [tier, subscriptionCanceled, isInTrial]);

    return (
        <StyledBeautifulDialog
            closeDialog={closeDialog}
        >
            <StyledDialogTitle>
                <img src={getStaticUrl("/images/beautifulai-logos/beautifulai-upgrade-logo.svg")} />
                <Gap10 />
                <div>
                    {getTitleText()}
                </div>
                {isInTrial &&
                    <p>You have {pluralize(" day", daysRemainingInTrial, " days")} left in your trial. <StartSubscriptionLink
                        onClick={handleStartSubscription}
                        data-test-id="change-plan-start-subscription-link"
                    >Start your subscription</StartSubscriptionLink></p>
                }
            </StyledDialogTitle>
            <StyledDialogContent>
                <FlexBox column left top width="200px" padding="20px" style={{
                    backgroundColor: proHighlighted ? "#F7FAFC" : "",
                    border: proHighlighted ? "1px solid #DAE9F0" : ""
                }}>
                    <FlexBox column gap={8} left fillWidth
                        style={{ borderBottom: "1px solid #EEE" }}>
                        <PlanName>Pro{(subscriptionCanceled && tier === WorkspacePlanTier.PRO) &&
                            <span style={{ color: themeColors.warning }}> - Canceled Plan</span>}</PlanName>
                        <PlanDescriptionGrid>
                            <PriceCell plan={WorkspacePlanTier.PRO}>${(proPrice / 12).toFixed(0)}</PriceCell>
                            <FlexBox column left>
                                <PlanDescriptionFirstRow>per user/mo</PlanDescriptionFirstRow>
                                <PlanDescriptionSecondRow>billed annually</PlanDescriptionSecondRow>
                            </FlexBox>
                        </PlanDescriptionGrid>
                        <ProActionButton
                            blue
                            data-test-id="change-plan-pro-action-button"
                            action={proButtonAction}
                            disabled={tier === WorkspacePlanTier.PRO && featuredPlan === WorkspacePlanTier.TEAM && !subscriptionCanceled}
                            onClick={() => handleProButtonAction(proButtonAction)}

                        >
                            {proButtonAction}
                        </ProActionButton>
                        <Gap20 />
                    </FlexBox>
                    <FlexBox column left fillWidth>
                        {FEATURES_BY_PLAN[WorkspacePlanTier.PRO].map(text => (
                            <FlexBox padding="12px 0px" fillWidth left style={{ borderBottom: "1px solid #EEEEEE" }}>
                                <ProIcon color="primary" />
                                <PlanFeature>{text}</PlanFeature>
                            </FlexBox>
                        ))}
                    </FlexBox>
                </FlexBox>
                <FlexBox column left top width="200px" padding="20px" style={{
                    backgroundColor: !proHighlighted ? "#F7FAFC" : "",
                    border: !proHighlighted ? "1px solid #DAE9F0" : ""
                }}>
                    <FlexBox column gap={8} left fillWidth
                        style={{ borderBottom: "1px solid #EEE" }}>
                        <PlanName>Team</PlanName>
                        <PlanDescriptionGrid>
                            <PriceCell plan={WorkspacePlanTier.TEAM}>${(teamPrice / 12).toFixed(0)}</PriceCell>
                            <FlexBox column left>
                                <PlanDescriptionFirstRow>per user/mo</PlanDescriptionFirstRow>
                                <PlanDescriptionSecondRow>billed annually</PlanDescriptionSecondRow>
                            </FlexBox>
                        </PlanDescriptionGrid>
                        <TeamActionButton
                            blue
                            data-test-id="change-plan-team-action-button"
                            action={teamButtonAction}
                            onClick={() => handlePlanButtonAction(teamButtonAction)}
                        >
                            {teamButtonAction}
                        </TeamActionButton>
                        <Gap20 />
                    </FlexBox>
                    <FlexBox column left fillWidth>
                        {FEATURES_BY_PLAN[WorkspacePlanTier.TEAM].map(text => (
                            <FlexBox padding="12px 0px" fillWidth left style={{ borderBottom: "1px solid #EEEEEE" }}>
                                <TeamIcon color="primary" />
                                <PlanFeature>{text}</PlanFeature>
                            </FlexBox>
                        ))}
                    </FlexBox>
                </FlexBox>
                <FlexBox column left top width="200px" padding="20px">
                    <FlexBox column gap={8} left fillWidth
                        style={{ borderBottom: "1px solid #EEE" }}>
                        <PlanName>Enterprise</PlanName>
                        <FlexBox column left>
                            <PlanDescriptionOnlyRow>Custom pricing available</PlanDescriptionOnlyRow>
                        </FlexBox>
                        <EnterpriseActionButton
                            blue
                            onClick={handleContactSales}
                            data-test-id="change-plan-enterprise-action-button"
                        >
                            {PlanButtonAction.CONTACT_SALES}
                        </EnterpriseActionButton>
                        <Gap20 />
                    </FlexBox>
                    <FlexBox column left fillWidth>
                        {FEATURES_BY_PLAN[WorkspacePlanTier.ENTERPRISE].map(text => (
                            <FlexBox padding="12px 0px" fillWidth left style={{ borderBottom: "1px solid #EEEEEE" }}>
                                <EnterpriseIcon color="primary" />
                                <PlanFeature>{text}</PlanFeature>
                            </FlexBox>
                        ))}
                    </FlexBox>
                </FlexBox>
            </StyledDialogContent>
            <StyledDialogActions>
                <Button
                    unframed
                    large
                    color={themeColors.ui_blue}
                    data-test-id="change-plan-close-dialog"
                    onClick={closeDialog}

                >
                    Not Now
                </Button>
            </StyledDialogActions>
        </StyledBeautifulDialog>
    );
};

export default WorkspaceController.withInitializedState(
    BillingController.withInitializedState(
        ChangePlanDialog
    )
);
